//#region src/services/llmDataStream/llmDataStreamEvents.ts
/**
* LLMDataStream Service Events
*/
const RPC_EVENT_STREAM_CREATED_IDENT = "org.aimystic.events.llmDataStream.created";
const RPC_EVENT_STREAM_UPDATED_IDENT = "org.aimystic.events.llmDataStream.updated";
const RPC_EVENT_STREAM_DELETED_IDENT = "org.aimystic.events.llmDataStream.deleted";
const RPC_EVENT_STREAM_CREATED = {
	ident: RPC_EVENT_STREAM_CREATED_IDENT,
	description: "Event emitted when a stream is created",
	params: [{
		name: "stream",
		type: "object",
		description: "The stream that was created"
	}]
};
const RPC_EVENT_STREAM_UPDATED = {
	ident: RPC_EVENT_STREAM_UPDATED_IDENT,
	description: "Event emitted when a stream is updated",
	params: [{
		name: "stream",
		type: "object",
		description: "The stream that was updated"
	}]
};
const RPC_EVENT_STREAM_DELETED = {
	ident: RPC_EVENT_STREAM_DELETED_IDENT,
	description: "Event emitted when a stream is deleted",
	params: [{
		name: "streamId",
		type: "string",
		description: "The ID of the stream that was deleted"
	}]
};
/**
* LLMHistoryEntry Service Events
*/
const RPC_EVENT_ENTRY_CREATED_IDENT = "org.aimystic.events.llmHistoryEntry.created";
const RPC_EVENT_ENTRY_DELETED_IDENT = "org.aimystic.events.llmHistoryEntry.deleted";
const RPC_EVENT_ENTRY_CREATED = {
	ident: RPC_EVENT_ENTRY_CREATED_IDENT,
	description: "Event emitted when an entry is created",
	params: [{
		name: "streamId",
		type: "string",
		description: "The ID of the stream"
	}, {
		name: "entry",
		type: "object",
		description: "The entry that was created"
	}]
};
const RPC_EVENT_ENTRY_DELETED = {
	ident: RPC_EVENT_ENTRY_DELETED_IDENT,
	description: "Event emitted when an entry is deleted",
	params: [
		{
			name: "streamId",
			type: "string",
			description: "The ID of the stream"
		},
		{
			name: "entryId",
			type: "string",
			description: "The ID of the entry that was deleted"
		},
		{
			name: "sequenceNum",
			type: "number",
			description: "The sequence number of the deleted entry"
		}
	]
};
/**
* DataStreamManager Service Events
* Events for stream manager operations (entry/content CRUD via manager)
*/
const RPC_EVENT_MANAGER_ENTRY_ADDED_IDENT = "org.aimystic.events.dataStreamManager.entryAdded";
const RPC_EVENT_MANAGER_ENTRY_REMOVED_IDENT = "org.aimystic.events.dataStreamManager.entryRemoved";
const RPC_EVENT_MANAGER_ENTRY_UPDATED_IDENT = "org.aimystic.events.dataStreamManager.entryUpdated";
const RPC_EVENT_MANAGER_ENTRY_FORKED_IDENT = "org.aimystic.events.dataStreamManager.entryForked";
const RPC_EVENT_MANAGER_CONTENT_ADDED_IDENT = "org.aimystic.events.dataStreamManager.contentAdded";
const RPC_EVENT_MANAGER_CONTENT_REMOVED_IDENT = "org.aimystic.events.dataStreamManager.contentRemoved";
const RPC_EVENT_MANAGER_CONTENT_UPDATED_IDENT = "org.aimystic.events.dataStreamManager.contentUpdated";
const RPC_EVENT_MANAGER_ENTRIES_BULK_REMOVED_IDENT = "org.aimystic.events.dataStreamManager.entriesBulkRemoved";
const RPC_EVENT_MANAGER_STREAM_DELTA_IDENT = "org.aimystic.events.dataStreamManager.streamDelta";
const RPC_EVENT_MANAGER_ENTRY_ADDED = {
	ident: RPC_EVENT_MANAGER_ENTRY_ADDED_IDENT,
	description: "Event emitted when an entry is added via DataStreamManager",
	groups: ["stream"],
	params: [{
		name: "streamId",
		type: "string",
		description: "The ID of the stream"
	}, {
		name: "entry",
		type: "object",
		description: "The entry that was added"
	}]
};
const RPC_EVENT_MANAGER_ENTRY_REMOVED = {
	ident: RPC_EVENT_MANAGER_ENTRY_REMOVED_IDENT,
	description: "Event emitted when an entry is removed via DataStreamManager",
	groups: ["stream"],
	params: [{
		name: "streamId",
		type: "string",
		description: "The ID of the stream"
	}, {
		name: "entryId",
		type: "string",
		description: "The ID of the entry that was removed"
	}]
};
const RPC_EVENT_MANAGER_ENTRY_UPDATED = {
	ident: RPC_EVENT_MANAGER_ENTRY_UPDATED_IDENT,
	description: "Event emitted when an entry is updated via DataStreamManager",
	groups: ["stream"],
	params: [{
		name: "streamId",
		type: "string",
		description: "The ID of the stream"
	}, {
		name: "entry",
		type: "object",
		description: "The updated entry"
	}]
};
const RPC_EVENT_MANAGER_ENTRY_FORKED = {
	ident: RPC_EVENT_MANAGER_ENTRY_FORKED_IDENT,
	description: "Event emitted when an entry is forked creating a new stream",
	groups: ["stream"],
	params: [
		{
			name: "sourceStreamId",
			type: "string",
			description: "The ID of the source stream"
		},
		{
			name: "newStreamId",
			type: "string",
			description: "The ID of the newly created stream"
		},
		{
			name: "forkedFromEntryId",
			type: "string",
			description: "The ID of the entry that was forked from"
		}
	]
};
const RPC_EVENT_MANAGER_CONTENT_ADDED = {
	ident: RPC_EVENT_MANAGER_CONTENT_ADDED_IDENT,
	description: "Event emitted when content is added to an entry via DataStreamManager",
	groups: ["stream"],
	params: [
		{
			name: "streamId",
			type: "string",
			description: "The ID of the stream"
		},
		{
			name: "entryId",
			type: "string",
			description: "The ID of the entry"
		},
		{
			name: "content",
			type: "object",
			description: "The content that was added"
		}
	]
};
const RPC_EVENT_MANAGER_CONTENT_REMOVED = {
	ident: RPC_EVENT_MANAGER_CONTENT_REMOVED_IDENT,
	description: "Event emitted when content is removed from an entry via DataStreamManager",
	groups: ["stream"],
	params: [
		{
			name: "streamId",
			type: "string",
			description: "The ID of the stream"
		},
		{
			name: "entryId",
			type: "string",
			description: "The ID of the entry"
		},
		{
			name: "contentId",
			type: "string",
			description: "The ID of the content that was removed"
		}
	]
};
const RPC_EVENT_MANAGER_CONTENT_UPDATED = {
	ident: RPC_EVENT_MANAGER_CONTENT_UPDATED_IDENT,
	description: "Event emitted when content is updated in an entry via DataStreamManager",
	groups: ["stream"],
	params: [
		{
			name: "streamId",
			type: "string",
			description: "The ID of the stream"
		},
		{
			name: "entryId",
			type: "string",
			description: "The ID of the entry"
		},
		{
			name: "content",
			type: "object",
			description: "The updated content"
		}
	]
};
const RPC_EVENT_MANAGER_ENTRIES_BULK_REMOVED = {
	ident: RPC_EVENT_MANAGER_ENTRIES_BULK_REMOVED_IDENT,
	description: "Event emitted when entries are bulk removed from a stream",
	groups: ["stream"],
	params: [{
		name: "streamId",
		type: "string",
		description: "The ID of the stream"
	}, {
		name: "entryIds",
		type: "array",
		description: "The IDs of the removed entries"
	}]
};
const RPC_EVENT_MANAGER_STREAM_DELTA = {
	ident: RPC_EVENT_MANAGER_STREAM_DELTA_IDENT,
	description: "Event emitted when streaming content delta is available",
	groups: ["stream"],
	params: [{
		name: "delta",
		type: "object",
		description: "The content delta object containing streamId, contentId, type, and delta data"
	}]
};

//#endregion
export { RPC_EVENT_ENTRY_CREATED, RPC_EVENT_ENTRY_CREATED_IDENT, RPC_EVENT_ENTRY_DELETED, RPC_EVENT_ENTRY_DELETED_IDENT, RPC_EVENT_MANAGER_CONTENT_ADDED, RPC_EVENT_MANAGER_CONTENT_ADDED_IDENT, RPC_EVENT_MANAGER_CONTENT_REMOVED, RPC_EVENT_MANAGER_CONTENT_REMOVED_IDENT, RPC_EVENT_MANAGER_CONTENT_UPDATED, RPC_EVENT_MANAGER_CONTENT_UPDATED_IDENT, RPC_EVENT_MANAGER_ENTRIES_BULK_REMOVED, RPC_EVENT_MANAGER_ENTRIES_BULK_REMOVED_IDENT, RPC_EVENT_MANAGER_ENTRY_ADDED, RPC_EVENT_MANAGER_ENTRY_ADDED_IDENT, RPC_EVENT_MANAGER_ENTRY_FORKED, RPC_EVENT_MANAGER_ENTRY_FORKED_IDENT, RPC_EVENT_MANAGER_ENTRY_REMOVED, RPC_EVENT_MANAGER_ENTRY_REMOVED_IDENT, RPC_EVENT_MANAGER_ENTRY_UPDATED, RPC_EVENT_MANAGER_ENTRY_UPDATED_IDENT, RPC_EVENT_MANAGER_STREAM_DELTA, RPC_EVENT_MANAGER_STREAM_DELTA_IDENT, RPC_EVENT_STREAM_CREATED, RPC_EVENT_STREAM_CREATED_IDENT, RPC_EVENT_STREAM_DELETED, RPC_EVENT_STREAM_DELETED_IDENT, RPC_EVENT_STREAM_UPDATED, RPC_EVENT_STREAM_UPDATED_IDENT };
//# sourceMappingURL=llmDataStreamEvents.js.map